/**
 * checkout_c7.js — Commerce7 Checkout, shared by every Commerce7 tenant.
 *
 * Two entry-point FLOWS share the same Commerce7 primitives below (cart id /
 * customer token / customer id, phone normalization, c7ProceedCheckout,
 * c7ChargeCheckout, cart-clear, generic error) but differ in which message
 * kinds they drive and whether there's a review step before payment. Country
 * / state selection is entirely a UI-component concern either way (ShipForm /
 * PickupForm free-text vs CheckoutForm's api.c7GetShippingRegions dropdown) —
 * this file never touches it.
 *
 * FLOW "split" (default — Valle della Pace, parity with old CHECKOUT object,
 * valledellapace/script.js 4594-4900 + submit handlers 6194-6700):
 *   ws-checkout
 *     → checkout-delivery (DeliveryChoice)         Ship / Pickup
 *       → ws-checkout-delivery {method}
 *         Ship (logged in, has saved addresses)
 *           → checkout-address-options (ShipAddressOptions)
 *             → ws-checkout-address-select {address} → proceedShip
 *             → ws-checkout-address-new {}            → checkout-ship-form
 *         Ship (guest, or no saved addresses) → checkout-ship-form (ShipForm) → ws-checkout-ship
 *         Pickup → checkout-pickup-form (PickupForm) → ws-checkout-pickup
 *           → checkout-payment (PaymentForm, straight to payment — no review)
 *             → ws-checkout-charge → order-confirmation (OrderConfirmation)
 *
 * FLOW "reviewed" (Lightning Rock Winery, parity with old CHECKOUT.cart,
 * RedConcierge/static/javascript/main.js):
 *   ws-checkout
 *     → checkout-options (base CheckoutOptions)     Ship / Pickup
 *       → ws-checkout-method {type}
 *         type:"ship" (logged in, has saved addresses)
 *           → checkout-address-options (ShipAddressOptions)
 *             → ws-checkout-address-select {address} → proceedReviewed
 *             → ws-checkout-address-new {}            → checkout-form {type:"ship"}
 *         → checkout-form {type}                     shippingForm/pickupForm
 *           → ws-checkout-submit {type, fields}
 *             → order-review {type, cart, details}   old showOrderDetails
 *               → ws-checkout-review-pay
 *                 → checkout-form {type:"payment"}    paymentOptions/chargeStepForm
 *                   → ws-checkout-charge → order-confirmation + clear cart
 *               → ws-checkout-edit {type, values}      "Edit" → back to checkout-form
 *
 * SAVED-ADDRESS STEP (both flows, parity with old CHECKOUT.cart.addressOptions):
 *   only shown when the customer is logged in (customerToken) AND
 *   api.c7GetAddresses returns at least one saved address — otherwise both
 *   flows go straight to their blank ship form, same as a guest. The picker
 *   rebuilds a shipTo object from the chosen saved address's fields (not a
 *   customerAddressId reference) so it flows through the exact same
 *   proceedShip/proceedReviewed core as a freshly typed form.
 *
 * AUTH: cart is anonymous (cart_id). customer_token (when logged in via
 *   account_c7) is passed through so C7 attaches the order to the customer and
 *   the saved-card path works. customer_id (state.user) drives saved-cards
 *   and saved addresses.
 *
 * KNOWN LIMITATION: the BE returns C7 errors as non-2xx envelopes; core/api.js
 *   throws on non-2xx without surfacing the body, so field-level C7 errors
 *   can't be shown yet — we show a generic message. (Old FE read
 *   res.c7_error.errors.)
 *
 * EXPORTS: init({ flow: "split" | "reviewed" } = {}) — idempotent.
 *   Each tenant variant calls this with its own flow; the event bus is global
 *   (core/state.js `on`/`emit` fan out to every listener), so only ONE flow
 *   may be initialized per session — never call init() with both flows.
 */

import * as api from "../../core/api.js";
import { on, emit, getState, setState, addMessage } from "../../core/state.js";
import { getTenantId } from "../../core/configAccessors.js";
import { error } from "../../core/env.js";

// ─── Shared primitives (both flows) ─────────────────────────────────────────

function getCartId() {
  const fromState = getState().cart?.id;
  if (fromState) return fromState;
  try {
    return localStorage.getItem(`ws_c7cart_${getTenantId() || "default"}`) || null;
  } catch {
    return null;
  }
}

function getToken() {
  return getState().user?.customerToken || null;
}

function getCustomerId() {
  return getState().user?.customerId || "";
}

function showText(text) {
  addMessage({ role: "bot", kind: "text", data: { text } });
}

function cleanField(value) {
  if (value == null) return "";
  const text = String(value).trim();
  return text === "undefined" || text === "null" ? "" : text;
}

// C7 requires phone numbers in E.164. Auto-prefix a bare 10-digit US number
// with +1 (countryCode defaults to US) so normal input doesn't 422; anything
// already starting with + is left as-is, and other input passes through for
// C7 to validate (its field error is now surfaced to the user).
function normalizePhone(raw, countryCode) {
  const v = String(raw || "").trim();
  if (!v || v.startsWith("+")) return v;
  const digits = v.replace(/\D/g, "");
  if ((countryCode || "US").toUpperCase() === "US" && digits.length === 10) {
    return `+1${digits}`;
  }
  return v;
}

// scope: short keyword a form's own error listener can filter on (e.g. old
// ShipForm only reacts when scope === "ship"). Both flows' forms show
// `message` inline on the form itself (split: formErr; reviewed:
// CheckoutForm's submit-error line) — no separate chat bubble needed.
function genericError(e, scope, fallback) {
  error(`[checkout_c7] ${scope} failed`, e);
  const msg = (e && e.message) ? e.message : fallback || "Sorry, something went wrong. Please try again.";
  emit("ws-checkout-error", { scope, message: msg });
}

// Saved addresses for the logged-in customer (old getSavedAddresses). Empty
// (not thrown) on any failure or when logged out — callers fall through to
// the blank ship form either way.
async function getSavedAddresses() {
  const token = getToken();
  const customerId = getCustomerId();
  if (!token || !customerId) return [];
  try {
    const res = await api.c7GetAddresses({ customerId, customerToken: token });
    return Array.isArray(res?.addresses) ? res.addresses : [];
  } catch (e) {
    error("[checkout_c7] saved addresses failed", e);
    return [];
  }
}

// Saved C7 address → the shipTo shape (old addressOptions submit handler's
// buildShipTo — rebuilds shipTo straight from the saved address fields
// rather than referencing the address id, so it works identically for both
// flows' proceedCheckout call).
function addressToShipTo(addr) {
  return {
    firstName: addr.firstName || "",
    lastName: addr.lastName || "",
    company: addr.company || "",
    address: addr.address || "",
    address2: addr.address2 || "",
    city: addr.city || "",
    stateCode: addr.stateCode || "",
    zipCode: addr.zipCode || "",
    countryCode: addr.countryCode || "US",
    phone: addr.phone || "",
  };
}

// Clear the cart after a successful order (old clearOrderDetails /
// clearCheckoutSteps).
function clearCart() {
  setState({ cart: { ...getState().cart, id: null, items: [], summary: null } });
  try {
    localStorage.removeItem(`ws_c7cart_${getTenantId() || "default"}`);
  } catch {
    /* storage blocked */
  }
  emit("ws-cart-count", { count: 0 });
}

// Payment submit (saved card or new card) → charge the cart. Identical shape
// for both flows (both UIs emit ws-checkout-charge {checkoutType, cardId?,
// cardData?}) and both show "order-confirmation" on success.
async function handleCharge({ checkoutType, cardId, cardData } = {}) {
  const cartId = getCartId();
  if (!cartId) {
    showText(getState().cart.emptyMessage);
    return;
  }
  const token = getToken();
  const tenderPayload =
    checkoutType === "saved_card"
      ? { tenderType: "Credit Card", creditCard: { id: cardId } }
      : { tenderType: "Credit Card", creditCard: { ...cardData } };

  emit("ws-chat-typing", { active: true });
  try {
    const res = await api.c7ChargeCheckout({
      cartId,
      checkoutType,
      tenderPayload,
      customerId: getCustomerId(),
      customerToken: token || "",
    });
    emit("ws-chat-typing", { active: false });
    const orderId = res?.orderId || res?.orderNumber || res?.id || "";
    if (!orderId) {
      genericError(null, "payment", "Payment failed. Please try again.");
      return;
    }
    showText("Your order placed successfully.");
    emit("ws-checkout-success", {});
    addMessage({ role: "bot", kind: "order-confirmation", data: { orderId } });
    clearCart();
    emit("ws-order-confirmed", { orderId });
  } catch (e) {
    emit("ws-chat-typing", { active: false });
    genericError(e, "payment", "Payment failed. Please try again.");
  }
}

// ─── Flow "split" (Valle della Pace) ────────────────────────────────────────

function startCheckoutSplit() {
  const cartId = getCartId();
  if (!cartId) {
    showText(getState().cart.emptyMessage);
    return;
  }
  addMessage({ role: "bot", kind: "checkout-delivery", data: {} });
}

async function handleDelivery({ method } = {}) {
  const loggedIn = !!getToken();
  if (method === "Pickup") {
    api.c7GetPickupLocations()
      .then((res) => {
        const locations = res?.pickupLocations || res?.pickup_locations || [];
        addMessage({ role: "bot", kind: "checkout-pickup-form", data: { loggedIn, locations } });
      })
      .catch((e) => {
        error("[checkout_c7] pickup locations failed", e);
        addMessage({ role: "bot", kind: "checkout-pickup-form", data: { loggedIn, locations: [] } });
      });
    return;
  }
  if (loggedIn) {
    const addresses = await getSavedAddresses();
    if (addresses.length) {
      showText("Please choose shipping address");
      addMessage({ role: "bot", kind: "checkout-address-options", data: { addresses } });
      return;
    }
  }
  addMessage({ role: "bot", kind: "checkout-ship-form", data: { loggedIn } });
}

async function showPayment() {
  const loggedIn = !!getToken();
  let savedCards = [];
  if (loggedIn) {
    const customerId = getCustomerId();
    if (customerId) {
      try {
        const res = await api.c7GetSavedCards(customerId);
        savedCards = res?.savedCards || [];
      } catch (e) {
        error("[checkout_c7] saved cards failed", e);
      }
    }
  }
  showText("Please provide your payment details.");
  addMessage({ role: "bot", kind: "checkout-payment", data: { loggedIn, savedCards } });
}

// Shared core: shipTo is already in C7 shape (either from the blank form
// minus its guest email, or rebuilt from a saved address) → proceedCheckout,
// then on to payment.
async function proceedShip({ shipTo, email }) {
  const cartId = getCartId();
  const token = getToken();
  try {
    const res = await api.c7ProceedCheckout({
      cartId,
      payload: { orderDeliveryMethod: "Ship", email, shipTo },
      customerToken: token || "",
    });
    if (res?.cart?.id || res?.id) {
      await showPayment();
    } else {
      emit("ws-checkout-error", { scope: "ship", message: "We couldn't set your shipping details. Please try again." });
    }
  } catch (e) {
    genericError(e, "ship");
  }
}

async function handleShip({ data } = {}) {
  const token = getToken();
  const email = token ? "" : data.email || "";
  // shipTo is the address fields minus the guest email.
  const shipTo = { ...data };
  delete shipTo.email;
  shipTo.phone = normalizePhone(shipTo.phone, shipTo.countryCode);
  await proceedShip({ shipTo, email });
}

async function handlePickup({ data } = {}) {
  const cartId = getCartId();
  const token = getToken();
  const email = token ? "" : data.email || "";
  try {
    const res = await api.c7ProceedCheckout({
      cartId,
      payload: {
        orderDeliveryMethod: "Pickup",
        email,
        pickupBy: {
          firstName: data.firstName,
          lastName: data.lastName,
          countryCode: data.countryCode,
          inventoryLocationId: data.inventoryLocationId || "",
        },
      },
      customerToken: token || "",
    });
    if (res?.cart?.id || res?.id) {
      await showPayment();
    } else {
      emit("ws-checkout-error", { scope: "pickup", message: "We couldn't set your pickup details. Please try again." });
    }
  } catch (e) {
    genericError(e, "pickup");
  }
}

// ─── Flow "reviewed" (Lightning Rock Winery) ────────────────────────────────

function startCheckoutReviewed() {
  const cartId = getCartId();
  if (!cartId) {
    showText(getState().cart.emptyMessage);
    return;
  }
  addMessage({ role: "bot", kind: "checkout-options", data: {} });
}

async function chooseMethod({ type } = {}) {
  if (type !== "ship" && type !== "pickup") return;
  if (type === "ship" && getToken()) {
    const addresses = await getSavedAddresses();
    if (addresses.length) {
      showText("Please choose shipping address");
      addMessage({ role: "bot", kind: "checkout-address-options", data: { addresses } });
      return;
    }
  }
  // Old shippingForm/pickupForm: a bot intro line before the form itself.
  showText(
    type === "pickup"
      ? "Please enter pickup details"
      : "Please enter your shipping details",
  );
  // Old pickupBtn + customer_token: pickupForm(false, { firstName, lastName })
  // — names autofilled from _CUSTOMER, not treated as Edit.
  const data = { type };
  if (type === "pickup" && getToken()) {
    const profile = getState().user?.profile || {};
    data.values = {
      firstName: profile.firstName || profile.first_name || "",
      lastName: profile.lastName || profile.last_name || "",
    };
  }
  addMessage({ role: "bot", kind: "checkout-form", data });
}

// Old showOrderDetails "Edit Address"/"Edit Pickup Details". Pickup always
// goes straight to a prefilled form (old pickupForm — no picker exists for
// pickup). Ship differs once a tender is attached to the cart: old code
// re-opens the saved-address picker instead of a prefilled form (the
// customer may want a different saved address, not just a correction) —
// only with no tender yet does it go straight to the form.
async function editDelivery({ type, values, hasTender } = {}) {
  if (type !== "ship" && type !== "pickup") return;
  if (type === "ship" && hasTender) {
    const addresses = await getSavedAddresses();
    if (addresses.length) {
      showText("Please choose shipping address");
      addMessage({ role: "bot", kind: "checkout-address-options", data: { addresses } });
    } else {
      showText("Please enter your shipping details");
      addMessage({ role: "bot", kind: "checkout-form", data: { type: "ship" } });
    }
    return;
  }
  addMessage({
    role: "bot",
    kind: "checkout-form",
    data: { type, values: values || {}, isEdit: true },
  });
}

// Old shippingForm/pickupForm fields → Commerce7's native shipTo/pickupBy
// shape (payload is a raw passthrough to the C7 API on the BE — see
// commerce7/serializers.py ProceedCheckoutSerializer).
function buildShipTo(fields) {
  return {
    firstName: fields.firstName,
    lastName: fields.lastName,
    company: fields.company || "",
    address: fields.address1,
    address2: fields.address2 || "",
    city: fields.city,
    stateCode: fields.province,
    zipCode: fields.zip,
    countryCode: fields.country,
    phone: normalizePhone(fields.phone, fields.country),
  };
}

// Old pickupForm never let the customer choose a location — it always
// defaulted to the first pickup location (pickupLocations[0]).
async function buildPickupBy(fields) {
  let inventoryLocationId = "";
  try {
    const res = await api.c7GetPickupLocations();
    const locations = Array.isArray(res?.pickupLocations) ? res.pickupLocations : [];
    inventoryLocationId = locations[0]?.id || "";
  } catch (e) {
    error("[checkout_c7] pickup locations failed", e);
  }
  return {
    firstName: fields.firstName,
    lastName: fields.lastName,
    countryCode: fields.country,
    inventoryLocationId,
  };
}

// Reverse of buildShipTo/buildPickupBy — the confirmed cart.shipTo/pickupBy
// the BE echoed back, mapped to our normalized field names so OrderReview and
// the checkout-form Edit round-trip can use them like any other `values`.
// Old showOrderDetails read straight from cartSummary.shipTo/.pickupBy (the
// confirmed cart), not the raw submitted form — same source here.
function shipToDetails(shipTo = {}, email) {
  return {
    firstName: shipTo.firstName || "",
    lastName: shipTo.lastName || "",
    company: shipTo.company || "",
    address1: shipTo.address || "",
    address2: shipTo.address2 || "",
    city: shipTo.city || "",
    province: shipTo.stateCode || "",
    zip: shipTo.zipCode || "",
    country: shipTo.countryCode || "",
    phone: shipTo.phone || "",
    email: email || "",
  };
}

function pickupByDetails(pickupBy = {}, email) {
  return {
    firstName: pickupBy.firstName || "",
    lastName: pickupBy.lastName || "",
    country: pickupBy.countryCode || "",
    email: email || "",
    address: pickupBy?.address,
    address1: pickupBy?.address1,
    address2: pickupBy?.address2
  };
}

// Shared core: deliveryTarget is already in C7 shipTo/pickupBy shape (either
// from the form's fields, or rebuilt from a saved address) → proceedCheckout,
// then on to the order review.
async function proceedReviewed({ type, deliveryTarget, email }) {
  const cartId = getCartId();
  if (!cartId) {
    showText(getState().cart.emptyMessage);
    return;
  }
  const token = getToken();
  const deliveryKey = type === "pickup" ? "pickupBy" : "shipTo";

  emit("ws-chat-typing", { active: true });
  try {
    const res = await api.c7ProceedCheckout({
      cartId,
      payload: {
        orderDeliveryMethod: type === "pickup" ? "Pickup" : "Ship",
        email,
        [deliveryKey]: deliveryTarget,
      },
      customerToken: token || "",
    });
    emit("ws-chat-typing", { active: false });
    const cart = res?.cart;
    if (!cart) {
      genericError(null, type, "We couldn't save your details. Please try again.");
      return;
    }
    emit("ws-checkout-success", {});
    showText("Please review your order summary.");
    const details =
      type === "pickup"
        ? pickupByDetails(cart.pickupBy, cart.email || email)
        : shipToDetails(cart.shipTo, cart.email || email);
    addMessage({ role: "bot", kind: "order-review", data: { type, cart, details } });
  } catch (e) {
    emit("ws-chat-typing", { active: false });
    genericError(e, type, "We couldn't save your details. Please try again.");
  }
}

// Ship/Pickup form submitted → set delivery details on the cart, then show
// the order review (old CHECKOUT.cart.showOrderDetails).
async function handleSubmitReviewed({ type, fields = {} } = {}) {
  const token = getToken();
  const email = token ? "" : cleanField(fields.email);
  const deliveryTarget =
    type === "pickup" ? await buildPickupBy(fields) : buildShipTo(fields);
  await proceedReviewed({ type, deliveryTarget, email });
}

// Order review "Continue to Payment" → old paymentOptions. No intro bot line
// here (old paymentOptions never showed one — "Please enter/update your card
// details" is chargeStepForm's own intro, shown only once CheckoutForm.js's
// saved-card check actually falls through to the blank form); adding one
// unconditionally here read as "you're about to fill in a form" even when a
// saved-card picker was about to render instead.
function goToPayment() {
  addMessage({ role: "bot", kind: "checkout-form", data: { type: "payment" } });
}

// ─── Saved-address step (both flows) ────────────────────────────────────────

// "+ Add new address" on the saved-address picker → fall through to the
// blank ship form each flow already has (old addressOptions' "new-ship"
// action button).
function handleAddressNew() {
  if (started === "reviewed") {
    showText("Please enter your shipping details");
    addMessage({ role: "bot", kind: "checkout-form", data: { type: "ship" } });
    return;
  }
  addMessage({ role: "bot", kind: "checkout-ship-form", data: { loggedIn: !!getToken() } });
}

// "Continue" on the saved-address picker → same proceedCheckout core each
// flow's manual form uses, just fed a shipTo rebuilt from the chosen saved
// address instead of freshly typed fields (old addressOptions submit
// handler). The customer is always logged in to reach this step, so there's
// no guest email to carry.
async function handleAddressSelect({ address } = {}) {
  if (!address) return;
  const shipTo = addressToShipTo(address);
  if (started === "reviewed") {
    await proceedReviewed({ type: "ship", deliveryTarget: shipTo, email: "" });
    return;
  }
  await proceedShip({ shipTo, email: "" });
}

// Pencil icon on the saved-address picker → old editShippingForm, prefilled.
function handleAddressEdit({ address } = {}) {
  if (!address) return;
  addMessage({ role: "bot", kind: "checkout-address-edit-form", data: { address } });
}

// Edit form submitted → update the saved C7 address, then back to the
// picker with the refreshed list (old parity: editShippingForm re-showed
// addressOptions after a successful save).
async function handleAddressUpdate({ addressId, payload } = {}) {
  if (!addressId) return;
  const customerId = getCustomerId();
  const token = getToken();
  try {
    await api.c7UpdateAddress({ customerId, addressId, addressPayload: payload, customerToken: token || "" });
  } catch (e) {
    genericError(e, "address", "We couldn't update that address. Please try again.");
    return;
  }
  const addresses = await getSavedAddresses();
  if (addresses.length) {
    showText("Please choose shipping address");
    addMessage({ role: "bot", kind: "checkout-address-options", data: { addresses } });
  } else {
    // No saved addresses left (shouldn't normally happen from an edit) — same
    // flow-aware blank-form fallback as "+ Add new address".
    handleAddressNew();
  }
}

// ─── Saved-card edit (Lightning Rock Winery's CheckoutForm, "reviewed" flow) ─

// Pencil icon on the saved-card picker → old paymentOptions' edit-action-btn
// → chargeStepForm(true, card, ...), a dedicated single-card form (no picker
// shown alongside it).
function handleCardEdit({ card } = {}) {
  if (!card) return;
  showText("Please update your card details");
  addMessage({ role: "bot", kind: "checkout-form", data: { type: "payment", values: { ...card }, isEdit: true } });
}

// Edit form submitted → update the saved C7 card, then back to the picker
// with the refreshed list (old: re-ran paymentOptions after a successful
// updateCreditCard).
async function handleCardUpdate({ cardId, payload } = {}) {
  if (!cardId) return;
  const customerId = getCustomerId();
  const token = getToken();
  try {
    await api.c7UpdateCard({ customerId, cardId, payload, customerToken: token || "" });
  } catch (e) {
    genericError(e, "card", "We couldn't update that card. Please try again.");
    return;
  }
  showText("Card updated successfully.");
  addMessage({ role: "bot", kind: "checkout-form", data: { type: "payment" } });
}

// ─── init ────────────────────────────────────────────────────────────────

let started = null;

export function init({ flow = "split" } = {}) {
  if (started) return;
  started = flow;

  // Shared by both flows.
  on("ws-checkout-charge", handleCharge);
  on("ws-checkout-address-new", handleAddressNew);
  on("ws-checkout-address-select", handleAddressSelect);
  on("ws-checkout-address-edit", handleAddressEdit);
  on("ws-checkout-address-update", handleAddressUpdate);
  on("ws-checkout-card-edit", handleCardEdit);
  on("ws-checkout-card-update", handleCardUpdate);

  if (flow === "reviewed") {
    on("ws-checkout", startCheckoutReviewed);
    on("ws-checkout-method", chooseMethod);
    on("ws-checkout-submit", handleSubmitReviewed);
    on("ws-checkout-edit", editDelivery);
    on("ws-checkout-review-pay", goToPayment);
    return;
  }

  on("ws-checkout", startCheckoutSplit);
  on("ws-checkout-delivery", handleDelivery);
  on("ws-checkout-ship", handleShip);
  on("ws-checkout-pickup", handlePickup);
}
